import { Button, Form, Input, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customerService } from "@/api/customerService";
import { messageService } from "@/api/messageService";
import { ZhTabs } from "@/components/common";
import { useUserSession } from "@/stores/session";
import { SafeCaptchaInput, SafeSettingFormShell } from "./SafeSettingFormParts";

interface CaptchaForm { captcha: string }
interface PasswordForm { newPassword: string; repeatPassword: string }
/** 手机号脱敏规则与旧页一致。 */
export function maskSafeMobile(mobile: string) { return mobile.replace(/(\d{3})\d{6}(\d{2})/, "$1******$2"); }

/** 更新密码两阶段表单：先验证手机号/邮箱，再提交新密码。 */
export function UpdatePasswordPage() {
    const mobile = useUserSession(state => state.mobile);
    const email = useUserSession(state => state.email);
    const [stage, setStage] = useState<"verify" | "reset">("verify");
    const [verifyType, setVerifyType] = useState(1);
    const [verifiedAccount, setVerifiedAccount] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [captchaForm] = Form.useForm<CaptchaForm>();
    const [passwordForm] = Form.useForm<PasswordForm>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
    const account = email && verifyType === 2 ? email : mobile;
    useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

    /** 发送身份验证码并启动 60 秒防重复倒计时。 */
    const sendCaptcha = async () => {
        if (countdown) return;
        try {
            await messageService.sendCaptchaOnUpdatePassword(account);
            setCountdown(60);
            timerRef.current = setInterval(() => setCountdown(value => {
                if (value <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
                return value - 1;
            }), 1000);
        } catch { void message.error("验证码发送失败，请稍后重试"); }
    };
    /** 身份验证通过后锁定本次修改使用的账号。 */
    const verify = async () => {
        const { captcha } = await captchaForm.validateFields();
        try {
            const valid = await messageService.checkCaptchaOnSafeSetting(account, captcha);
            if (!valid) { void message.error("验证码错误"); return; }
            setVerifiedAccount(account);
            setStage("reset");
        } catch { void message.error("身份验证失败，请稍后重试"); }
    };
    /** 校验密码强度和一致性后提交，并同步安全设置展示时间。 */
    const resetPassword = async () => {
        const values = await passwordForm.validateFields();
        try {
            const result = await customerService.updatePassword({ ...values, account: verifiedAccount });
            if (!result) return;
            useUserSession.setState({ passwordUpdateTime: dayjs().format("YYYY-MM-DD HH:mm:ss") });
            void message.success(`密码${searchParams.get("type") === "bind" ? "绑定" : "修改"}成功`);
            navigate("/me/safeSetting");
        } catch { void message.error("密码更新失败，请稍后重试"); }
    };

    return (
        <SafeSettingFormShell>
            {stage === "verify" ? (
                <div className="update-password-form">
                    <div className="update-password-title">
                        验证身份
                        <div className="update-password-tips">为确保是您本人操作，请先验证身份</div>
                    </div>
                    {email ? (
                        <div className="update-password-tabs">
                            <ZhTabs
                                active={verifyType}
                                options={[{ label: "手机号验证", value: 1 }, { label: "邮箱验证", value: 2 }]}
                                onChange={value => { setVerifyType(value); captchaForm.resetFields(); }}
                            />
                        </div>
                    ) : null}
                    <Form
                        className={email ? "verify-form has-email" : "verify-form"}
                        colon={false}
                        form={captchaForm}
                        labelAlign="right"
                    >
                        <Form.Item label={email && verifyType === 2 ? "邮箱" : "手机号"}>
                            <span>{email && verifyType === 2 ? email : maskSafeMobile(mobile)}</span>
                        </Form.Item>
                        <Form.Item name="captcha" label="验证码" rules={[{ required: true, message: "请输入验证码" }]}>
                            <SafeCaptchaInput countdown={countdown} onSend={() => void sendCaptcha()} />
                        </Form.Item>
                    </Form>
                    <div className="update-password-submit">
                        <Button type="primary" onClick={() => void verify()}>立即验证</Button>
                    </div>
                </div>
            ) : (
                <div className="update-password-form reset-form">
                    <div className="update-password-title">
                        {searchParams.get("type") === "setting" ? "设置" : "修改"}登陆密码
                    </div>
                    <Form
                        className="reset-password-fields"
                        colon={false}
                        form={passwordForm}
                        labelAlign="right"
                    >
                        <Form.Item
                            name="newPassword"
                            label="新的登录密码"
                            rules={[
                                { required: true, message: "请输入新密码" },
                                { pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,16}$/, message: "请设置6-16位包含字母、数字的密码" }
                            ]}
                        >
                            <Input.Password placeholder="请输入新密码" />
                        </Form.Item>
                        <Form.Item
                            name="repeatPassword"
                            label="再次输入"
                            dependencies={["newPassword"]}
                            rules={[
                                { required: true, message: "请再次输入新密码" },
                                ({ getFieldValue }) => ({
                                    validator: (_, value) => !value || value === getFieldValue("newPassword")
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("两次密码输入不一致"))
                                })
                            ]}
                        >
                            <Input.Password placeholder="请再次输入新密码" />
                        </Form.Item>
                    </Form>
                    <div className="update-password-submit">
                        <Button type="primary" onClick={() => void resetPassword()}>确定</Button>
                    </div>
                </div>
            )}
        </SafeSettingFormShell>
    );
}
